import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { isEqual } from 'lodash';
import { Store } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { CartControlExtension, ExtensionComponentType } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { CheckoutReducer } from '@app/pages/checkout-app/checkout-store/reducers';
import { RulesDynamicFormService } from '@app/sharedModule/components/rules-dynamic-form/rules-dynamic-form.service';
import {
  buildUpsellTokenOptions,
  withUpsellTokenOptions,
} from '@app/pages/checkout-app/checkout-layout/main/components/extensions/shared/upsell-token-options';
import { ConfigurationsFormGroup, ExceptionFormGroup } from '../../cart-control-form.component';
import { CartControlExceptionRules } from '../../../../config';
import { createCartControlExceptionFormGroup } from '../../../../exception-form.factory';

interface ExceptionChip {
  label: string;
  source: CartControlExtension.ItemSource;
}

// Friendly labels per item source, used by the quick-add chips.
const SOURCE_LABELS: Record<string, string> = {
  [ExtensionComponentType.GIFT_UNLOCK]: 'PDQ Gift Items',
  [ExtensionComponentType.CHECKOUT_UPSELL]: 'PDQ Checkout Upsell',
  [ExtensionComponentType.CUSTOM_UPSELL]: 'PDQ Digital Upsell',
  [CartControlExtension.ANY_UPSELL]: 'Any upsell / gift',
};

@Component({
  selector: 'cart-control-form-exceptions',
  templateUrl: './cart-control-form-exceptions.component.html',
  styleUrl: './cart-control-form-exceptions.component.scss',
  standalone: false,
})
export class CartControlFormExceptionsComponent implements OnInit, OnDestroy {
  @Input() configurationsGroup: FormGroup<ConfigurationsFormGroup>;
  @Input() shopCurrencySymbol: string;

  // Reassigned in ngOnInit once the "Specific upsell" token options are loaded from the store.
  exceptionRules = CartControlExceptionRules;
  readonly exceptionEntities = CartControlExceptionRules.map((rule) => rule.entity);

  // Quick-add chips; tapping one scaffolds an exception pre-seeded with an "Item source is any of" condition.
  readonly chips: ExceptionChip[] = [
    { label: SOURCE_LABELS[ExtensionComponentType.GIFT_UNLOCK], source: ExtensionComponentType.GIFT_UNLOCK },
    { label: SOURCE_LABELS[ExtensionComponentType.CHECKOUT_UPSELL], source: ExtensionComponentType.CHECKOUT_UPSELL },
    { label: SOURCE_LABELS[ExtensionComponentType.CUSTOM_UPSELL], source: ExtensionComponentType.CUSTOM_UPSELL },
    { label: SOURCE_LABELS[CartControlExtension.ANY_UPSELL], source: CartControlExtension.ANY_UPSELL },
  ];

  private readonly destroy$ = new Subject<void>();
  // Per-exception "Customers can" enable/disable subscription, keyed by its FormGroup so it can be
  // torn down on removal (using destroy$ would leak removed FormGroups until navigation).
  private readonly actionsSubs = new Map<FormGroup<ExceptionFormGroup>, Subscription>();

  constructor(
    private readonly rulesDynamicFormService: RulesDynamicFormService,
    private readonly store: Store<CheckoutReducer.State>,
  ) {}

  ngOnInit(): void {
    // Prefill the "Specific upsell" dropdown with the merchant's upsell extensions.
    this.store
      .select(CheckoutReducer.selectExtensions)
      .pipe(map(buildUpsellTokenOptions), distinctUntilChanged(isEqual), takeUntil(this.destroy$))
      .subscribe((tokenOptions) => {
        this.exceptionRules = withUpsellTokenOptions(CartControlExceptionRules, tokenOptions);
      });

    // Show one starter block on first open; empty exceptions are stripped on save (see handleSave),
    // so this never persists as a match-everything rule.
    if (this.exceptions.length === 0) {
      this.exceptions.push(createCartControlExceptionFormGroup(this.rulesDynamicFormService));
    }

    this.exceptions.controls.forEach((exception) => this.bindActionsState(exception));

    // The parent prunes this shared FormArray on save without our removal paths, so reconcile on
    // every change to tear down subscriptions for externally-removed exceptions.
    this.exceptions.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.reconcileActionsSubs());
  }

  ngOnDestroy(): void {
    this.actionsSubs.forEach((sub) => sub.unsubscribe());
    this.actionsSubs.clear();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get exceptions(): FormArray<FormGroup<ExceptionFormGroup>> {
    return this.configurationsGroup.get('exceptions') as FormArray<FormGroup<ExceptionFormGroup>>;
  }

  // True once the exception has at least one condition with a selected entity.
  isExceptionConfigured(exception: FormGroup<ExceptionFormGroup>): boolean {
    return exception.controls.conditions.controls.some((condition) => !!condition.get('entity')?.value);
  }

  // True once an exception already carries that source (used to dim the chip).
  isChipAdded(source: CartControlExtension.ItemSource): boolean {
    return this.exceptions.controls.some((exception) => exception.controls.source.value === source);
  }

  addException(source?: CartControlExtension.ItemSource): void {
    const exception = createCartControlExceptionFormGroup(this.rulesDynamicFormService, undefined, source);
    this.exceptions.push(exception);
    this.bindActionsState(exception);
    // A chip scaffolds a configured exception, so drop the empty starter panel; "+ Add exception"
    // (no source) keeps existing panels so the merchant can fill in a blank one.
    if (source) {
      this.removeEmptyExceptionsExcept(exception);
    }
    this.exceptions.markAsDirty();
  }

  // Remove unconfigured exceptions (e.g. the untouched starter) except the one just added.
  // Iterates backwards so removeAt indices stay valid.
  private removeEmptyExceptionsExcept(keep: FormGroup<ExceptionFormGroup>): void {
    for (let i = this.exceptions.length - 1; i >= 0; i--) {
      const exception = this.exceptions.at(i);
      if (exception !== keep && !this.isExceptionConfigured(exception)) {
        this.teardownException(exception);
        this.exceptions.removeAt(i);
      }
    }
  }

  // Enable the "Customers can" actions only while the exception has a configured condition. Toggling
  // the reactive control (not just a [disabled] input) is what actually blocks interaction.
  private bindActionsState(exception: FormGroup<ExceptionFormGroup>): void {
    const sync = (): void => {
      const actions = exception.controls.actions;
      if (this.isExceptionConfigured(exception)) {
        if (actions.disabled) {
          actions.enable({ emitEvent: false });
        }
      } else if (actions.enabled) {
        actions.disable({ emitEvent: false });
      }
    };
    sync();
    this.actionsSubs.set(exception, exception.controls.conditions.valueChanges.subscribe(sync));
  }

  // Unsubscribe an exception's actions subscription; call before removeAt in every removal path.
  private teardownException(exception: FormGroup<ExceptionFormGroup>): void {
    this.actionsSubs.get(exception)?.unsubscribe();
    this.actionsSubs.delete(exception);
  }

  // Safety net for external removals (e.g. the parent's save-time prune): tear down subscriptions for
  // any tracked exception no longer in the FormArray. Idempotent.
  private reconcileActionsSubs(): void {
    if (this.exceptions.length === this.actionsSubs.size) {
      return;
    }
    const current = new Set<FormGroup<ExceptionFormGroup>>(this.exceptions.controls);
    for (const exception of [...this.actionsSubs.keys()]) {
      if (!current.has(exception)) {
        this.teardownException(exception);
      }
    }
  }

  // There is no per-exception trash icon: removing the last rule (remaining === 0) drops the whole
  // exception. Deferred via microtask so we don't tear down the form while it's still emitting.
  onRuleRemoved(event: { index: number; remaining: number }, exception: FormGroup<ExceptionFormGroup>): void {
    if (event?.remaining !== 0) {
      return;
    }
    queueMicrotask(() => {
      const index = this.exceptions.controls.indexOf(exception);
      if (index > -1) {
        this.teardownException(exception);
        this.exceptions.removeAt(index);
        this.exceptions.markAsDirty();
      }
    });
  }
}
